import { 
  PrdDocument, 
  PrdSection, 
  PrdRequirement, 
  UserStory,
  PrdTemplate,
  ValidationError,
  PrdValidationResult 
} from '../types';

export function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

export function createEmptyDocument(title: string = 'Untitled PRD'): PrdDocument {
  const now = new Date();
  return {
    id: generateId(),
    title,
    version: '1.0.0',
    status: 'draft',
    author: {
      id: 'system',
      name: 'System',
    },
    createdAt: now,
    updatedAt: now,
    sections: [],
    requirements: [],
    userStories: [],
    tags: [],
  };
}

export function createSectionFromTemplate(
  type: PrdSection['type'],
  title: string,
  order: number,
  defaultContent?: string
): PrdSection {
  return {
    id: generateId(),
    type,
    title,
    content: defaultContent || '',
    order,
  };
}

export function applyTemplate(document: PrdDocument, template: PrdTemplate): PrdDocument {
  const sections = template.sections.map((section, index) => 
    createSectionFromTemplate(
      section.type,
      section.title,
      index,
      section.defaultContent
    )
  );

  return {
    ...document,
    sections,
    metadata: {
      ...document.metadata,
      templateId: template.id,
      templateName: template.name,
    },
  };
}

export function validateDocument(document: PrdDocument): PrdValidationResult {
  const errors: ValidationError[] = [];
  const warnings: ValidationError[] = [];

  // Check title
  if (!document.title || document.title.trim().length === 0) {
    errors.push({
      field: 'title',
      message: 'Document title is required',
      severity: 'error',
    });
  }

  // Check sections
  if (document.sections.length === 0) {
    warnings.push({
      field: 'sections',
      message: 'Document has no sections',
      severity: 'warning',
    });
  }

  // Check for empty sections
  document.sections.forEach((section, index) => {
    if (!section.content || section.content.trim().length === 0) {
      warnings.push({
        field: `sections[${index}].content`,
        message: `Section "${section.title}" is empty`,
        severity: 'warning',
      });
    }
  });

  // Check requirements
  if (document.requirements.length === 0) {
    warnings.push({
      field: 'requirements',
      message: 'No requirements defined',
      severity: 'warning',
    });
  }

  // Validate requirements
  document.requirements.forEach((req, index) => {
    if (!req.title || req.title.trim().length === 0) {
      errors.push({
        field: `requirements[${index}].title`,
        message: 'Requirement title is required',
        severity: 'error',
      });
    }
    if (!req.description || req.description.trim().length === 0) {
      warnings.push({
        field: `requirements[${index}].description`,
        message: `Requirement "${req.title}" has no description`,
        severity: 'warning',
      });
    }
  });

  // Calculate score
  const totalChecks = 10;
  const passedChecks = totalChecks - errors.length - (warnings.length * 0.5);
  const score = Math.max(0, Math.round((passedChecks / totalChecks) * 100));

  return {
    valid: errors.length === 0,
    errors,
    warnings,
    score,
  };
}

export function exportToMarkdown(document: PrdDocument): string {
  let markdown = `# ${document.title}\n\n`;
  
  // Metadata
  markdown += `**Version:** ${document.version}\n`;
  markdown += `**Status:** ${document.status}\n`;
  markdown += `**Author:** ${document.author.name}\n`;
  markdown += `**Last Updated:** ${document.updatedAt.toISOString()}\n\n`;

  // Table of Contents
  markdown += `## Table of Contents\n\n`;
  document.sections.forEach((section, index) => {
    markdown += `${index + 1}. [${section.title}](#${section.title.toLowerCase().replace(/\s+/g, '-')})\n`;
  });
  markdown += '\n';

  // Sections
  document.sections.forEach(section => {
    markdown += `## ${section.title}\n\n`;
    markdown += `${section.content}\n\n`;
  });

  // Requirements
  if (document.requirements.length > 0) {
    markdown += `## Requirements\n\n`;
    
    const grouped = groupRequirementsByType(document.requirements);
    
    Object.entries(grouped).forEach(([type, reqs]) => {
      markdown += `### ${capitalizeFirst(type)} Requirements\n\n`;
      
      reqs.forEach(req => {
        markdown += `#### ${req.title}\n\n`;
        markdown += `**Priority:** ${req.priority}\n`;
        markdown += `**Status:** ${req.status || 'draft'}\n\n`;
        markdown += `${req.description}\n\n`;
        
        if (req.acceptanceCriteria && req.acceptanceCriteria.length > 0) {
          markdown += `**Acceptance Criteria:**\n`;
          req.acceptanceCriteria.forEach(criteria => {
            markdown += `- ${criteria}\n`;
          });
          markdown += '\n';
        }
      });
    });
  }

  // User Stories
  if (document.userStories.length > 0) {
    markdown += `## User Stories\n\n`;
    
    document.userStories.forEach(story => {
      markdown += `### ${story.title}\n\n`;
      markdown += `**As a** ${story.asA},\n`;
      markdown += `**I want** ${story.iWant},\n`;
      markdown += `**So that** ${story.soThat}\n\n`;
      
      if (story.acceptanceCriteria.length > 0) {
        markdown += `**Acceptance Criteria:**\n`;
        story.acceptanceCriteria.forEach(criteria => {
          markdown += `- ${criteria}\n`;
        });
        markdown += '\n';
      }
      
      markdown += `**Priority:** ${story.priority}\n`;
      if (story.effort) {
        markdown += `**Effort:** ${story.effort} points\n`;
      }
      markdown += '\n';
    });
  }

  return markdown;
}

export function groupRequirementsByType(requirements: PrdRequirement[]): Record<string, PrdRequirement[]> {
  return requirements.reduce((acc, req) => {
    if (!acc[req.type]) {
      acc[req.type] = [];
    }
    acc[req.type].push(req);
    return acc;
  }, {} as Record<string, PrdRequirement[]>);
}

export function groupRequirementsByPriority(requirements: PrdRequirement[]): Record<string, PrdRequirement[]> {
  return requirements.reduce((acc, req) => {
    if (!acc[req.priority]) {
      acc[req.priority] = [];
    }
    acc[req.priority].push(req);
    return acc;
  }, {} as Record<string, PrdRequirement[]>);
}

export function calculateProgress(document: PrdDocument): {
  total: number;
  completed: number;
  percentage: number;
} {
  const requirements = document.requirements;
  const total = requirements.length;
  const completed = requirements.filter(r => r.status === 'implemented').length;
  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
  
  return { total, completed, percentage };
}

export function searchDocument(
  document: PrdDocument,
  query: string
): {
  sections: PrdSection[];
  requirements: PrdRequirement[];
  userStories: UserStory[];
} {
  const lowerQuery = query.toLowerCase();
  
  const sections = document.sections.filter(section =>
    section.title.toLowerCase().includes(lowerQuery) ||
    section.content.toLowerCase().includes(lowerQuery)
  );
  
  const requirements = document.requirements.filter(req =>
    req.title.toLowerCase().includes(lowerQuery) ||
    req.description.toLowerCase().includes(lowerQuery) ||
    req.tags?.some(tag => tag.toLowerCase().includes(lowerQuery))
  );
  
  const userStories = document.userStories.filter(story =>
    story.title.toLowerCase().includes(lowerQuery) ||
    story.asA.toLowerCase().includes(lowerQuery) ||
    story.iWant.toLowerCase().includes(lowerQuery) ||
    story.soThat.toLowerCase().includes(lowerQuery) ||
    story.tags?.some(tag => tag.toLowerCase().includes(lowerQuery))
  );
  
  return { sections, requirements, userStories };
}

export function generateTableOfContents(sections: PrdSection[]): Array<{
  id: string;
  title: string;
  level: number;
}> {
  return sections
    .sort((a, b) => a.order - b.order)
    .map(section => ({
      id: section.id,
      title: section.title,
      level: 1,
    }));
}

export function capitalizeFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function formatVersion(major: number, minor: number, patch: number): string {
  return `${major}.${minor}.${patch}`;
}

export function incrementVersion(version: string, type: 'major' | 'minor' | 'patch' = 'patch'): string {
  const [major, minor, patch] = version.split('.').map(Number);
  
  switch (type) {
    case 'major':
      return formatVersion(major + 1, 0, 0);
    case 'minor':
      return formatVersion(major, minor + 1, 0);
    case 'patch':
    default:
      return formatVersion(major, minor, patch + 1);
  }
}

export const defaultTemplates: PrdTemplate[] = [
  {
    id: 'standard',
    name: 'Standard PRD',
    description: 'A comprehensive PRD template for most products',
    sections: [
      { type: 'overview', title: 'Executive Summary', required: true },
      { type: 'background', title: 'Background & Context', required: true },
      { type: 'objectives', title: 'Goals & Objectives', required: true },
      { type: 'scope', title: 'Scope & Constraints', required: true },
      { type: 'requirements', title: 'Functional Requirements', required: true },
      { type: 'requirements', title: 'Non-Functional Requirements', required: false },
      { type: 'user-stories', title: 'User Stories', required: true },
      { type: 'acceptance-criteria', title: 'Acceptance Criteria', required: true },
      { type: 'timeline', title: 'Timeline & Milestones', required: true },
      { type: 'risks', title: 'Risks & Mitigation', required: false },
    ],
  },
  {
    id: 'agile',
    name: 'Agile PRD',
    description: 'Lightweight PRD for agile teams',
    sections: [
      { type: 'overview', title: 'Product Vision', required: true },
      { type: 'objectives', title: 'Sprint Goals', required: true },
      { type: 'user-stories', title: 'User Stories', required: true },
      { type: 'acceptance-criteria', title: 'Definition of Done', required: true },
      { type: 'scope', title: 'Out of Scope', required: false },
    ],
  },
  {
    id: 'technical',
    name: 'Technical PRD',
    description: 'PRD template for technical products and APIs',
    sections: [
      { type: 'overview', title: 'Technical Overview', required: true },
      { type: 'background', title: 'System Architecture', required: true },
      { type: 'requirements', title: 'Technical Requirements', required: true },
      { type: 'requirements', title: 'API Specifications', required: true },
      { type: 'requirements', title: 'Security Requirements', required: true },
      { type: 'acceptance-criteria', title: 'Testing Strategy', required: true },
      { type: 'timeline', title: 'Implementation Plan', required: true },
      { type: 'appendix', title: 'Technical Appendix', required: false },
    ],
  },
];